import type { Transaction } from '@domain/finance'
import type { Receipt } from '@domain/receipt'
import type { ReceiptRepository } from '@domain/ports/receipt-repository'
import type { TransactionRepository } from '@domain/ports/finance-repository'

/**
 * Vincular uma nota a uma transação, e levar a categoria da nota junto.
 *
 * ## Uma função só, porque agora são duas portas
 *
 * O vínculo nascia na tela da nota (escolher a transação dela). Com o
 * "Selecionar nota existente" no detalhe da transação, ele passa a acontecer
 * pelo outro lado também — e a regra da categoria copiada em dois lugares é a
 * regra que um dia discorda de si mesma.
 *
 * ## Só quando a transação está sem categoria
 *
 * Sobrescrever em silêncio uma escolha feita à mão é o tipo de ajuda que só se
 * descobre depois, num número que mudou sozinho — e a rosca do mês mudaria
 * junto.
 *
 * As duas escritas não são uma transação atômica de propósito: se a segunda
 * falhar, a nota fica vinculada e sem categorizar a compra, que é exatamente o
 * estado de antes desta funcionalidade. O contrário — categorizar sem vincular
 * — é que seria estranho, e por isso o vínculo vem primeiro.
 *
 * Devolve a categoria que a transação recebeu, ou `null`: quem mostra a
 * transação se corrige sem reler.
 */
export async function vincularNota(
  repos: {
    receiptRepository: Pick<ReceiptRepository, 'linkTransaction'>
    transactionRepository: Pick<TransactionRepository, 'updateEnrichment'>
  },
  nota: Pick<Receipt, 'id' | 'categoryId'>,
  transacao: Transaction,
): Promise<string | null> {
  await repos.receiptRepository.linkTransaction(nota.id, transacao.id)
  if (nota.categoryId === null || transacao.enrichment.categoryId !== null) return null
  await repos.transactionRepository.updateEnrichment(transacao.id, { categoryId: nota.categoryId })
  return nota.categoryId
}
